import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { SiteHeader } from "@/components/SiteHeader";

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';

export function LegalFooter() {
  return (
    <footer className="mt-[96px] flex w-full flex-col items-center">
      <hr className="border-0 h-px bg-burgundy w-full max-w-[720px]" />
      <p
        className="mt-6 font-bold uppercase tracking-[0.1em] text-burgundy text-[16px] text-center"
        style={{ fontFamily: MONO }}
      >
        WHO KNEW
      </p>
      <p
        className="mt-3 font-bold uppercase tracking-[0.05em] text-[10px] text-muted-foreground text-center"
        style={{ fontFamily: MONO }}
      >
        © 2026 WHO KNEW. ALL RIGHTS RESERVED.
      </p>
      <div
        className="mt-4 mb-6 font-bold uppercase tracking-[0.05em] text-[10px] text-burgundy text-center"
        style={{ fontFamily: MONO }}
      >
        <Link to="/privacy" className="link-hit hover:underline">Privacy</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="link-hit hover:underline">Terms</Link>
        <span className="mx-2">·</span>
        <Link to="/refunds" className="link-hit hover:underline">Refunds</Link>
        <span className="mx-2">·</span>
        <Link to="/teams" className="link-hit hover:underline">For teams</Link>
      </div>

    </footer>
  );
}

export function LegalSection({
  label,
  children,
  first = false,
}: {
  label: string;
  children: ReactNode;
  first?: boolean;
}) {
  return (
    <>
      {!first && (
        <div
          style={{
            marginTop: "48px",
            marginBottom: "48px",
            height: "1px",
            backgroundColor: "#E8E1D3",
          }}
        />
      )}
      <section>
        <p
          className="font-bold uppercase tracking-[0.05em] text-burgundy"
          style={{ fontFamily: MONO, fontSize: "12px" }}
        >
          {label}
        </p>
        <div
          className="text-ink"
          style={{
            fontFamily: SERIF,
            fontSize: "18px",
            lineHeight: 1.6,
            marginTop: "20px",
          }}
        >
          {children}
        </div>
      </section>
    </>
  );
}

export function LegalAside({ children }: { children: ReactNode }) {
  return (
    <p
      className="italic"
      style={{
        fontFamily: SERIF,
        fontSize: "16px",
        color: "#5A534A",
        lineHeight: 1.6,
        marginTop: "24px",
      }}
    >
      {children}
    </p>
  );
}

export function LegalPage({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader />
      <main
        className="mx-auto w-full"
        style={{
          maxWidth: "720px",
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingTop: "128px",
        }}
      >
        <p
          className="font-bold uppercase tracking-[0.05em] text-burgundy"
          style={{ fontFamily: MONO, fontSize: "11px" }}
        >
          {eyebrow}
        </p>
        <h1
          className="text-[28px] md:text-[40px] font-bold leading-[1.15] text-ink"
          style={{ marginTop: "24px" }}
        >
          {title}
        </h1>
        <p
          className="italic"
          style={{
            fontFamily: SERIF,
            fontSize: "14px",
            color: "#5A534A",
            marginTop: "12px",
          }}
        >
          Last updated May 2026.
        </p>
        <div
          style={{
            marginTop: "24px",
            width: "60px",
            height: "2px",
            backgroundColor: "#8B1E1E",
          }}
        />
        <div style={{ marginTop: "48px", paddingBottom: "96px" }}>{children}</div>
      </main>
      <LegalFooter />
    </div>
  );
}

export function L({ children }: { children: ReactNode }) {
  // simple paragraph helper
  return <p style={{ marginTop: "16px" }}>{children}</p>;
}

export function LList({ items }: { items: ReactNode[] }) {
  return (
    <ul style={{ marginTop: "16px", paddingLeft: "20px", listStyleType: "disc" }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginTop: i === 0 ? 0 : "8px" }}>
          {it}
        </li>
      ))}
    </ul>
  );
}
